<template>
    <div>
        <OnboardingStories
            v-if="showStories && onboarding && onboarding.stories"
            :onboarding="onboarding"
            @stories-end="closeStories"
        />

        <f7-sheet
            :opened="shown"
            class="onboarding-sheet"
            swipe-to-close
            @sheet:closed="closeModal()"
        >
            <template v-if="onboarding && onboarding.id">
                <div>
                    <div class="onboarding-sheet__header-banner">
                        <div class="onboarding-sheet__swipe-line-banner"></div>
                    </div>
                    <div class="onboarding-sheet__banner_picture">
                        <template>
                            <video
                                playsinline
                                autoplay
                                muted
                                loop
                                width="100%"
                                :poster="imageSrc(onboarding.image)"
                                @click="openStories"
                            >
                                <source :src="imageSrc(onboarding.image)" type="video/mp4" />
                            </video>
                        </template>
                    </div>
                </div>
            </template>
        </f7-sheet>
    </div>
</template>

<script>
    import { mapState, mapGetters } from 'vuex';
    import axios from '@/plugins/axios-silent';
    import { getDeviceId, sendAppMetricaEvent } from '@/js/utils/service-helpers';
    import indexedDB from '@/plugins/indexedDB';
    import OnboardingStories from '@/components/common/OnboardingStories';

    export default {
        name: 'Onboarding',
        components: {
            OnboardingStories,
        },
        data() {
            return {
                shown: false,
                onboarding: null,
                showStories: false,
                isProcessing: false, // флаг для предотвращения повторных показов
                lastShowTime: 0, // время последнего показа
                surveyRequested: false, // запуск опроса только один раз на показ онбординга
            };
        },
        props: {
            canOpen: {
                type: Boolean,
                default: false,
            },
        },

        computed: {
            ...mapState('onboarding', ['onboardings']),
            ...mapState('auth', ['user']),
            ...mapGetters('remoteConfig', ['getAbTestVariant', 'isInAbTest', 'getConfigValue', 'getAbTestConfig']),
        },

        watch: {
            canOpen(val) {
                if (val) {
                    const routeName = this?.$f7?.views?.main?.router?.url || '/';
                    this.tryShowOnboarding(routeName);
                }
            },
        },

        async created() {
            this.shown = false;

            await this.$store.dispatch('onboarding/fetchOnboardings');

            this.$root.$on('onboardingShow', async ({ routeName = '/' }) => {
                await this.tryShowOnboarding(routeName);
            });
            this.$root.$on('onboardingClose', async () => {
                this.closeModal();
                this.showStories = false;
            });
        },
        methods: {
            async tryShowOnboarding(routeName = '/') {
                const now = Date.now();
                if (this.isProcessing || (now - this.lastShowTime < 1000)) {
                    this.$emit('waiting');
                    return;
                }
                this.isProcessing = true;
                let localOnboardings;
                try {
                    this.onboarding = null;
                    localOnboardings = await indexedDB.get('mircity-onboardings') || {};
                } catch (error) {
                    console.error('Ошибка при показе онбординга:', error);
                    this.isProcessing = false;
                    this.$emit('waiting');
                    return;
                }
                const filteredOnboardings = this.onboardings.filter(item => item.router === routeName);
                if (filteredOnboardings?.length) {
                    let index = 0;
                    while (!this.onboarding && index < filteredOnboardings.length) {
                        const onboarding = filteredOnboardings[index];
                        const shouldShowOnboarding = await this.shouldShowOnboardingByAbTest(onboarding);
                        if (shouldShowOnboarding) {
                            if (!localOnboardings[onboarding.id]) {
                                localOnboardings[onboarding.id] = { shows: 0 };
                            }
                            if (localOnboardings[onboarding.id].shows < onboarding.limit_show_count) {
                                this.onboarding = onboarding;
                                this.surveyRequested = false;
                            }
                        }
                        index++;
                    }
                }
                if (this.onboarding?.id) {
                    if (this.onboarding.show_banner) {
                        this.shown = true;
                    } else {
                        this.openStories();
                    }
                    localOnboardings[this.onboarding.id].shows += 1;
                    await indexedDB.set('mircity-onboardings', localOnboardings);
                    this.$emit('processing', true);
                } else {
                    this.$emit('waiting');
                }
                this.lastShowTime = Date.now();
                this.isProcessing = false;
            },
            async isNewUserByDevice() {
                try {
                    // Авторизованный пользователь точно не новый
                    if (this.user && this.user.id) return false;
                    const deviceId = await getDeviceId();

                    if (!deviceId) return true;
                    const { data, status } = await axios.get(`/front/customer/by-device`, { params: { device_id: deviceId } });
                    
                    if (status === 200 && data) {
                        return data.exists === false;
                    }
                } catch (e) {}
                return true;
            },
            imageSrc(picture) {
                return picture ? `${process.env.VUE_APP_IMAGES_URL}/${picture}` : '';
            },
            openStories() {
                if (!this.onboarding?.stories) {
                    console.warn('Cannot open stories: onboarding or stories is null/empty');
                    return;
                }
                
                this.showStories = true;
                this.shown = false;
            },
            closeStories() {
                this.showStories = false;
                this.$emit('processing', false);
                this.checkPostSurvey();
            },
            closeModal() {
                this.shown = false;
                this.$emit('processing', false);
                setTimeout(() => {
                    window.dispatchEvent(new Event('resize'));
                }, 300);
            },
            async checkPostSurvey() {
                try {
                    if (!this.onboarding?.id) return;
                    if (this.surveyRequested) return; // уже запускали попытку
                    
                    const deviceId = await getDeviceId();

                    const firstOpenTs = await indexedDB.get('mircity-first-open-ts');
                    const params = [];
                    if (deviceId) params.push(`device_id=${encodeURIComponent(deviceId)}`);
                    if (firstOpenTs) params.push(`first_open_ts=${firstOpenTs}`);
                    // Всегда показываем связанный опрос за онбордингом в рамках A/B теста "first-pay"
                    const isFirstPay = this.onboarding?.title === 'first-pay';
                    if (isFirstPay) params.push('force=1');
                    const url = `/front/onboarding/${this.onboarding.id}/survey${params.length ? ('?' + params.join('&')) : ''}`;
                    this.surveyRequested = true; // устанавливаем перед запросом, чтобы исключить гонки
                    const { data, status } = await axios.get(url);
                    if (status === 200 && data?.survey) {
                        this.$root.$emit('surveyFlowStart', data.survey);
                    }
                } catch (e) {
                    // 204 -> нет опроса
                }
            },
            
            async shouldShowOnboardingByAbTest(onboarding) {
                // Специальная логика для старого A/B теста "first-pay"
                const testKey = onboarding.title;
                if (testKey === 'first-pay') {
                    const isNewUser = await this.isNewUserByDevice();
                    if (!isNewUser) return false;
                    if (!this.isInAbTest(testKey)) return false;
                    const configValue = this.getConfigValue(testKey);
                    
                    const abTestData = this.$store.state.remoteConfig.abTests[testKey];
                    const alternativeConfigValue = abTestData?.config?.[testKey];
                    const finalConfigValue = configValue !== null ? configValue : alternativeConfigValue;
                    return finalConfigValue === true;
                }

                // Новая функциональность: глобальный A/B тест "onboarding_3_0"
                // Если onboarding === false, исключаем только указанный onboarding_id.
                const globalAbConfig = this.getAbTestConfig('onboarding_3_0') || {};
                if (globalAbConfig.onboarding === false && typeof globalAbConfig.onboarding_id === 'number') {
                    if (onboarding.id === globalAbConfig.onboarding_id) {
                        return false;
                    }
                }
                return true;
            }
        },
    };
</script>

<style lang="scss">
.onboarding-sheet {
    min-height: 100px;
    z-index: 12500;
    height: auto;
    bottom: calc(65px + env(safe-area-inset-bottom));
    bottom: calc(65px + constant(safe-area-inset-bottom));
    --f7-sheet-bg-color: #fefefe;
    background-color: #fefefe;

    .sheet-modal-inner {
        padding: 20px !important;
    }

    &__header-banner {
        display: flex;
        justify-content: center;
        align-items: center;
    }
    &__swipe-line-banner {
        width: 40px;
        height: 4px;
        background: rgba(169, 178, 191, 1);
        border-radius: 40px;
        margin-bottom: 10px;
    }

    &__banner_picture {
        position: relative;
        width: 100%;
        overflow: hidden;
        background-position: center center;
        background-repeat: no-repeat;
        background-size: cover;
        border-radius: 10px;

        video {
            width: 100%;
            height: 100%;
            object-fit: cover;
            cursor: pointer;
        }
    }
}
</style>
